// ─────────────────────────────────────────────────────────────
//  Account-Setup-Protokoll – Konfiguration der Setup-Phasen & Schritte.
//
//  Das Account-Setup-Protokoll ist die Schritt-fuer-Schritt-Anleitung, mit der
//  der Setup-Manager das technische SalesGod-Setup eines Kunden durchfuehrt.
//  Es besteht aus DREI Phasen mit je mehreren Schritten (siehe Onboarding-Flow).
//
//  WICHTIG – eine einzige Quelle der Wahrheit:
//   - Jeder Schritt hat eine stabile `id` und ist gleichzeitig ein
//     Checklisten-Punkt am Kundenobjekt (customers/<slug>/checklist/<id>).
//   - RUECKWAERTS-KOMPATIBILITAET: Schritte, die einem frueheren (gröberen)
//     Setup-Checklisten-Punkt entsprechen, behalten BEWUSST dessen alte ID
//     (z. B. `setup_domain`, `setup_warmup`). Dadurch tragen sich bereits
//     gesetzte Haken bestehender Kunden automatisch ins neue Protokoll ein –
//     ohne Migration. Nur die feineren, wirklich neuen Schritte bekommen neue
//     IDs (`setup_p1_*` / `setup_p2_*` / `setup_p3_*`) und starten leer.
//   - Genau diese Punkte erscheinen 1:1 in der Sales Pipeline (Setup-Schritt,
//     To-Do-Liste), auf der Research-Seite des Kontakts (Setup-Tab) und auf der
//     teilbaren Account-Setup-Seite (/setup/:slug). Wer irgendwo abhakt, hakt
//     ueberall ab – live.
//
//  Dieses Modul wird genutzt von:
//   - structureConfig.js  (baut daraus CHECKLISTS.setup -> Sales Pipeline)
//   - AccountSetupForm.js  (oeffentliche, passwortgeschuetzte Setup-Seite)
//   - Admin/Protocool.js   (Dashboard-Tab "Account-Setup-Protokoll")
//   - Research/ResearchPage.js (Setup-Fortschritt fuer den Kunden)
//   - functions/index.js   (Item-IDs muessen mit ACCOUNT_SETUP_ITEM_IDS synchron sein)
// ─────────────────────────────────────────────────────────────

// Felder pro Schritt:
//  - id          : stabile Checklisten-ID (customers/<slug>/checklist/<id>)
//  - num         : Schritt-Nummer innerhalb der Phase (nur Anzeige)
//  - title       : interner Titel (Setup-Manager-Sicht)
//  - desc        : Bullet-Zeilen mit Anleitung (Setup-Manager-Sicht)
//  - links       : optionale Hilfs-Links [{ label, url }]
//  - clientLabel : kundenfreundliche Formulierung (Research-Seite)
export const ACCOUNT_SETUP_PHASES = [
  {
    id: 'phase1',
    order: 1,
    label: 'Domain, E-Mail-Accounts & Warmup',
    duration: 'ca. 20 Min',
    accent: '#7c9df0',
    intro: 'Technisches Setup wird vom Setup-Manager durchgeführt. Alle nötigen Infos liegen im Research Center bereit.',
    steps: [
      {
        id: 'setup_domain',
        num: 1,
        title: 'Domain kaufen',
        clientLabel: 'Versand-Domain wird gekauft',
        desc: [
          'Domain wird für den Kunden gekauft.',
          'Ideal: ähnlich wie die Hauptdomain. Statt .de → .com, .online, .info …',
          'Alternative: Bindestrich zwischen den Namen.',
        ],
        links: [{ label: 'hostinger.com/domain-name-search', url: 'https://www.hostinger.com/domain-name-search' }],
      },
      {
        id: 'setup_postfaecher',
        num: 2,
        title: 'Postfach kaufen',
        clientLabel: 'Postfächer werden eingerichtet',
        desc: [
          'E-Mail-Postfach wird gekauft.',
          'Falls mehrere Postfächer nötig sind, Namensvarianten anlegen (z. B. bei Hans Huber: h.huber@…, hans@…, huber.hans@…, hans.h@…).',
        ],
        links: [{ label: 'hpanel.hostinger.com/emails', url: 'https://hpanel.hostinger.com/emails' }],
      },
      {
        id: 'setup_p1_salesgod_account',
        num: 3,
        title: 'SalesGod Account erstellen',
        clientLabel: 'SalesGod-Account wird erstellt',
        desc: [
          'Dient mit einem gekauften E-Mail-Account von Hostinger.',
          '1. Account erstellen auf BonusClub.link.',
          '2. E-Mail über die Hostinger-Webmail bestätigen.',
        ],
        links: [
          { label: 'BonusClub.link', url: 'https://bonusclub.link' },
          { label: 'mail.hostinger.com', url: 'https://mail.hostinger.com/' },
        ],
      },
      {
        id: 'setup_p1_account_unlock',
        num: 4,
        title: 'SalesGod Account vom Master-Account freischalten',
        clientLabel: 'Account wird freigeschaltet',
        desc: ['Den neuen Account vom Master-Account aus freischalten.'],
        links: [{ label: 'bonusclub.link/teamadmin/unlock', url: 'https://bonusclub.link/teamadmin/unlock' }],
      },
      {
        id: 'setup_p1_email_accounts',
        num: 5,
        title: 'E-Mail-Accounts in SalesGod anlegen',
        clientLabel: 'E-Mail-Accounts werden angelegt',
        desc: [
          'Settings → Email → Email Accounts.',
          'E-Mail-Accounts mit Passwort anlegen.',
          'Namen des Senders korrekt (Vorname + Nachname) eingeben, z. B. „Hans Huber".',
          'Settings: „Skip weekends" aktivieren. Nicht senden von 16h–08h.',
        ],
      },
      {
        id: 'setup_warmup',
        num: 6,
        title: 'Warmup aktivieren',
        clientLabel: 'E-Mail-Warmup wird aktiviert',
        desc: ['Settings → Email → Warmup.', 'Inbox aktivieren.'],
      },
    ],
  },
  {
    id: 'phase2',
    order: 2,
    label: 'Detail-Setup',
    duration: '',
    accent: '#d8a85a',
    intro: 'Feinschliff des Accounts: Signatur, Team, Zustellung und Safe-Guard.',
    steps: [
      {
        id: 'setup_signatur',
        num: 1,
        title: 'E-Mail-Signatur / Footer',
        clientLabel: 'E-Mail-Signatur wird erstellt',
        desc: [
          'Signatur wird für jeden Sender erstellt.',
          'Entweder nach Vorgaben des Kunden (z. B. dieselbe Signatur wie bei normalen Mails) oder custom über unsere Templates.',
          'Logo des Kunden eventuell notwendig.',
        ],
      },
      {
        id: 'setup_team_member',
        num: 2,
        title: 'Outreach-Manager hinzufügen',
        clientLabel: 'Zuständiges Team wird zugeteilt',
        desc: [
          'Team-Members wie z. B. der Outreach-Manager werden zum Account hinzugefügt.',
          'Settings → Admin → Team.',
        ],
      },
      {
        id: 'setup_p2_testmail',
        num: 3,
        title: 'Test-Mail an Inbox senden',
        clientLabel: 'Zustellung wird getestet',
        desc: [
          'Du sendest eine Mail an den Account, den du angelegt hast, und siehst, ob sie ankommt.',
          'Achtung: Oft kommt die 1. Mail noch nicht an, sondern erst ab der 2.',
        ],
      },
      {
        id: 'setup_p2_safeguard',
        num: 4,
        title: 'Safe-Guard einrichten',
        clientLabel: 'Safe-Guard wird eingerichtet',
        desc: [
          'So einrichten, dass miese Mails abgefangen werden, bevor wir sie verschicken.',
          'Wichtig: nicht zu streng einstellen.',
        ],
      },
    ],
  },
  {
    id: 'phase3',
    order: 3,
    label: 'Listen & Blacklist',
    duration: 'ca. 20 Min',
    accent: '#4cb89a',
    intro: 'Listen & Blacklists werden hinzugefügt. Alle nötigen Infos liegen im Research Center bereit.',
    steps: [
      {
        id: 'setup_blacklist',
        num: 1,
        title: 'Blacklist hinzufügen',
        clientLabel: 'Blacklist wird hinterlegt',
        desc: [
          'Kontakte von der Blacklist werden unter Settings → Safety → Blacklist als Liste hochgeladen.',
          'Vorsichtig und gewissenhaft durchführen → Blacklist ist heikel, wenn falsch importiert.',
        ],
      },
      {
        id: 'setup_p3_custom_felder',
        num: 2,
        title: 'Custom-Felder in Tabelle hinzufügen',
        clientLabel: 'Custom-Felder werden angelegt',
        desc: [
          'Anschauen, welche Felder zusätzlich für den Outreach wichtig sind und ein Tabellen-Feld brauchen.',
        ],
      },
      {
        id: 'setup_kontaktliste',
        num: 3,
        title: 'Target-Liste(n) hinzufügen',
        clientLabel: 'Kontaktliste wird eingespielt',
        desc: [
          'Kontakt mit Abdullah aufnehmen.',
          'Liste wird sorgfältig hinzugefügt.',
          'Wichtige Felder werden korrekt zugewiesen.',
        ],
      },
      {
        id: 'setup_p3_cleaner',
        num: 4,
        title: 'Cleaner prüfen',
        clientLabel: 'Cleaning wird geprüft',
        desc: [
          'Haben wir Cleaner, die exakt unsere Zielgruppe cleanen?',
          'Durchtesten, ob der Cleaning-Mechanismus sauber funktioniert.',
        ],
      },
      {
        id: 'setup_p3_research',
        num: 5,
        title: 'Research prüfen',
        clientLabel: 'Research wird geprüft',
        desc: [
          'Haben wir Research, die exakt unsere Zielgruppe trifft?',
          'Durchtesten, ob der Research-/Cleaning-Mechanismus sauber funktioniert.',
        ],
      },
      {
        id: 'setup_p3_testmail',
        num: 6,
        title: 'Test-Mail senden',
        clientLabel: 'Finale Test-Mails werden versendet',
        desc: [
          '1. Eine Test-Mail an dich selbst senden.',
          '2. Eine Test-Mail an Julian senden.',
          '3. Feedback einholen.',
        ],
      },
    ],
  },
];

// Flache Liste aller Schritte (mit Phasenbezug), in Reihenfolge.
export const ACCOUNT_SETUP_STEPS = ACCOUNT_SETUP_PHASES.flatMap((phase) =>
  phase.steps.map((step) => ({ ...step, phaseId: phase.id, phaseLabel: phase.label }))
);

// Alle Schritt-IDs (= Checklisten-Item-IDs). Muss mit ACCOUNT_SETUP_ITEM_IDS in
// functions/index.js synchron bleiben (Server validiert nur diese IDs).
export const ACCOUNT_SETUP_ITEM_IDS = ACCOUNT_SETUP_STEPS.map((s) => s.id);
const ACCOUNT_SETUP_ID_SET = new Set(ACCOUNT_SETUP_ITEM_IDS);

export const isAccountSetupItem = (id) => ACCOUNT_SETUP_ID_SET.has(id);

// Fortschritt über alle Phasen: { done, total, pct }.
export function accountSetupCompletion(checklist) {
  const map = checklist || {};
  const total = ACCOUNT_SETUP_ITEM_IDS.length;
  const done = ACCOUNT_SETUP_ITEM_IDS.filter((id) => !!map[id]).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return { done, total, pct };
}

// Fortschritt einer einzelnen Phase: { done, total }.
export function phaseCompletion(phase, checklist) {
  const map = checklist || {};
  const steps = (phase && phase.steps) || [];
  const done = steps.filter((s) => !!map[s.id]).length;
  return { done, total: steps.length };
}

// Baut den IMMER gleich strukturierten Share-Link zur Account-Setup-Seite.
export const accountSetupUrlFor = (origin, slug) => `${origin}/setup/${slug}`;
